import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import { type FieldProps, Field, useFormikContext } from 'formik';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';

import Tag from '@fd/ui/atoms/TagV2';
import FormItemLayout from '@fd/ui/Forms/FormItemLayout';
import Select, { InputActionMeta, OptionType } from '@fd/ui/Select/Select';

import { useAppSelector } from '../../../../custom-hooks/useAppSelector';
import { formikValidate } from '../../../../helpers/validation';
import { useAvailableAlphaSenders } from '../../hooks/useAvailableAlphaSenders';
import { useValidatedSenderCountries } from '../../hooks/useValidatedSenderCountries';
import { FormValues } from '../GeneralSettingsForm';

const StyledContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  paddingRight: theme.spacing(2),
}));

const StyledPills = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
}));

type InnerProps = MappedProps;
type OuterProps = {
  isFlipdishStaff: boolean;
};
type Props = InnerProps & OuterProps;

const SenderIdField = ({ isFlipdishStaff, translate }: Props) => {
  const theme = useTheme();
  const formik = useFormikContext<FormValues>();
  const countryCode = useAppSelector((state) => state.currentApp.CountryId) ?? '';
  const selectedSenderId = formik.values.SMSRestaurantName ?? '';

  const [searchQuery, setSearchQuery] = useState('');
  // Last value committed via picking an autocomplete option (survives later searches).
  const [committedSenderId, setCommittedSenderId] = useState<string | null>(null);
  // Alpha sender lookups are for Flipdish staff only; everyone else sees the saved sender read-only.
  const { data, isFetching, isSuccess, setSearchTerm } = useAvailableAlphaSenders(countryCode, {
    enabled: isFlipdishStaff,
  });
  const { data: countryRecords, isFetching: isCountriesFetching } = useValidatedSenderCountries(
    selectedSenderId,
    { enabled: isFlipdishStaff }
  );

  const fetchedSenderIds = new Set((data?.items ?? []).map((item) => item.senderId));
  const options: OptionType[] = Array.from(fetchedSenderIds).map((senderId) => ({
    label: senderId,
    value: senderId,
  }));

  // Promote a pre-existing form value only once it appears in fetched autocomplete results.
  useEffect(() => {
    if (selectedSenderId && fetchedSenderIds.has(selectedSenderId)) {
      setCommittedSenderId(selectedSenderId);
    }
  }, [data?.items, selectedSenderId]);

  const isAllowedSenderId = (value: string) =>
    fetchedSenderIds.has(value) || value === committedSenderId;

  const countryCodes = Array.from(
    new Set(
      (countryRecords ?? [])
        .map((record) => record.isoCountryCode?.trim())
        .filter((code): code is string => !!code)
    )
  ).sort();

  const validateNameField = (value: string) => {
    // Non-staff users cannot edit this field; do not block unrelated General Settings saves.
    if (!isFlipdishStaff) {
      return;
    }

    const formatError = formikValidate.SMSRestaurantName(value ?? '');
    if (formatError) {
      return formatError;
    }

    // Only enforce the allow-list after a successful available-senders response.
    // Distinguishes empty success (reject unknown) from failed/pending fetch (defer).
    if (!isSuccess) {
      return;
    }

    if (value && !isAllowedSenderId(value)) {
      return <Translate id="Not_an_available_sender" />;
    }

    return;
  };

  return (
    <FormItemLayout
      label={translate('Sender_ID')}
      description={translate('The_name_your_customers_will_see_when_receiving_SMS')}
    >
      <Field name="SMSRestaurantName" validate={validateNameField}>
        {({ field, form }: FieldProps) => {
          const { errors, isSubmitting, touched } = form;
          const fieldError = errors[field.name] as string | undefined;
          const showError = !!fieldError && !!touched[field.name];

          const isAllowedValue = !!field.value && isAllowedSenderId(field.value);
          // Without the lookups there is nothing to check against: show the saved sender read-only.
          const isReadOnlyValue = !isFlipdishStaff && !!field.value;
          const currentValue: OptionType | null =
            isAllowedValue || isReadOnlyValue ? { label: field.value, value: field.value } : null;

          const handleSelectChange = (selected: OptionType | null) => {
            const nextValue = (selected?.value as string) ?? '';

            // Reject anything that did not come from the current autocomplete options.
            if (nextValue && !fetchedSenderIds.has(nextValue)) {
              return;
            }

            setCommittedSenderId(nextValue || null);
            form.setFieldValue(field.name, nextValue);
            setSearchQuery('');
          };

          const handleInputChange = (inputValue: string, meta: InputActionMeta) => {
            if (meta.action === 'input-change') {
              setSearchQuery(inputValue);
            }
            return inputValue;
          };

          return (
            <StyledContainer>
              <Select
                dataFd="alpha-sender-select"
                dropdownIcon="search"
                error={showError}
                fieldError={showError ? fieldError : undefined}
                filterOption={() => true}
                inputValue={currentValue ? undefined : searchQuery}
                isClearable
                isDisabled={isSubmitting || !isFlipdishStaff}
                isLoading={isFetching}
                onChange={handleSelectChange}
                onInputChange={handleInputChange}
                options={options}
                placeholder={translate('No_Sender_ID_configured')}
                setSearchQuery={setSearchTerm}
                value={currentValue}
                variant="standard"
              />
              {!isCountriesFetching && isAllowedValue && countryCodes.length > 0 && (
                <StyledPills data-fd="alpha-sender-country-pills">
                  {countryCodes.map((code) => (
                    <Tag key={code} ownerState={{ backgroundColor: theme.palette.grey[300] }}>
                      {code}
                    </Tag>
                  ))}
                </StyledPills>
              )}
            </StyledContainer>
          );
        }}
      </Field>
    </FormItemLayout>
  );
};

type MappedProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => ({
  translate: getTranslate(state.locale),
});

export default connect(mapStateToProps)(SenderIdField);
